/** LW2-compatible project name / timezone helpers for account settings. */

export const PROJECT_NAME_FORBIDDEN_PATTERN = new RegExp('[*\\[\\];:"\\\\|.<>/?]');
export const PROJECT_NAME_ALLOWED_PATTERN = '^[^*\\[\\];:"\\\\|.<>/?]*$';
export const PROJECT_NAME_FORBIDDEN_MESSAGE =
  'Please enter alphabets or digits, special characters [*[\\];:"|<.>/?] are not allowed';
export const PROJECT_NAME_DUPLICATE_MESSAGE =
  'Sorry, a project already exists with that name, please try a different name';
export const PROJECT_NAME_REQUIRED_MESSAGE =
  'Project name cannot be left blank, please enter a unique project name';

export const PROJECT_NAME_MIN_LENGTH = 3;
export const PROJECT_NAME_MAX_LENGTH = 50;
export const PROJECT_NAME_MIN_LENGTH_MESSAGE =
  'Invalid project name, please enter at least 3 characters';
export const PROJECT_NAME_MAX_LENGTH_MESSAGE = 'Project name cannot exceed 50 characters';

export const DEFAULT_PROJECT_TIMEZONE = {
  name: 'Asia/Kolkata GMT(+5:30)',
  offset: -330,
  timezone: 'Asia/Kolkata'
};

export const PROJECT_COUNTRY_LIST: string[] = [
  'Afghanistan', 'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola', 'Anguilla', 'Antarctica',
  'Antigua and Barbuda', 'Argentina', 'Armenia', 'Aruba', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas',
  'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bermuda', 'Bhutan',
  'Bolivia', 'Bonaire, Sint Eustatius and Saba', 'Bosnia and Herzegovina', 'Botswana', 'Bouvet Island',
  'Brazil', 'British Indian Ocean Territory', 'Brunei Darussalam', 'Bulgaria', 'Burkina Faso', 'Burundi',
  'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Cayman Islands', 'Central African Republic', 'Chad',
  'Chile', 'China', 'Christmas Island', 'Cocos (Keeling) Islands', 'Colombia', 'Comoros', 'Congo',
  'Cook Islands', 'Costa Rica', 'Croatia', 'Cuba', 'Curaçao', 'Cyprus', 'Czechia', "Côte d'Ivoire",
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
  'Equatorial Guinea', 'England', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
  'Falkland Islands [Malvinas]', 'Faroe Islands', 'Fiji', 'Finland', 'France', 'French Guiana',
  'French Polynesia', 'French Southern Territories', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Gibraltar', 'Greece', 'Greenland', 'Grenada', 'Guadeloupe', 'Guam', 'Guatemala', 'Guernsey', 'Guinea',
  'Guinea-Bissau', 'Guyana', 'Haiti', 'Heard Island and McDonald Islands', 'Holy See', 'Honduras',
  'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Isle of Man',
  'Israel', 'Italy', 'Jamaica', 'Japan', 'Jersey', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait',
  'Kyrgyzstan', "Lao People's Democratic Republic", 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya',
  'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macao', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives',
  'Mali', 'Malta', 'Marshall Islands', 'Martinique', 'Mauritania', 'Mauritius', 'Mayotte', 'Mexico',
  'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Montserrat', 'Morocco', 'Mozambique',
  'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Caledonia', 'New Zealand', 'Nicaragua',
  'Niger', 'Nigeria', 'Niue', 'Norfolk Island', 'North Korea', 'Northern Mariana Islands', 'Norway',
  'Oman', 'Pakistan', 'Palau', 'Palestine, State of', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Pitcairn', 'Poland', 'Portugal', 'Puerto Rico', 'Qatar', 'Republic of North Macedonia',
  'Romania', 'Russian Federation', 'Rwanda', 'Réunion', 'Saint Barthélemy',
  'Saint Helena, Ascension and Tristan da Cunha', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Martin',
  'Saint Pierre and Miquelon', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
  'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Sint Maarten', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Georgia and the South Sandwich Islands', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka',
  'Sudan', 'Suriname', 'Svalbard and Jan Mayen', 'Sweden', 'Switzerland', 'Syrian Arab Republic', 'Taiwan',
  'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tokelau', 'Tonga',
  'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Turks and Caicos Islands', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom of Great Britain and Northern Ireland',
  'United States Minor Outlying Islands', 'United States of America', 'Uruguay', 'Uzbekistan', 'Vanuatu',
  'Venezuela', 'Viet Nam', 'Virgin Islands (British)', 'Virgin Islands (U.S.)', 'Wallis and Futuna',
  'Western Sahara', 'Yemen', 'Zambia', 'Zimbabwe', 'Åland Islands'
];

export function hasForbiddenProjectNameChars(value: string): boolean {
  return PROJECT_NAME_FORBIDDEN_PATTERN.test(value || '');
}

/** Trims the name so surrounding blanks never count towards length or uniqueness. */
export function normalizeProjectName(value: string): string {
  return (value || '').trim();
}

/** Dynamic form keys come from labels (`audience`), not schema property names (`audience_type`). */
export function getProjectTypeFromForm(formValue: any): number {
  const audience = String(formValue?.audience_type ?? formValue?.audience ?? '').trim().toLowerCase();
  return audience === 'employee' ? 3 : 1;
}

export function isDuplicateProjectName(value: string, existingNames: string[]): boolean {
  const name = normalizeProjectName(value).toLowerCase();
  if (!name) {
    return false;
  }
  return existingNames.some((existing) => normalizeProjectName(existing).toLowerCase() === name);
}

export function validateProjectNameLive(
  value: string,
  existingNames: string[]
): { valid: boolean; message?: string } {
  // An untouched field is reported by Validators.required, so stay silent for it.
  if (!value) {
    return { valid: true };
  }
  const name = normalizeProjectName(value);
  if (!name) {
    return { valid: false, message: PROJECT_NAME_REQUIRED_MESSAGE };
  }
  if (hasForbiddenProjectNameChars(name)) {
    return { valid: false, message: PROJECT_NAME_FORBIDDEN_MESSAGE };
  }
  if (name.length < PROJECT_NAME_MIN_LENGTH) {
    return { valid: false, message: PROJECT_NAME_MIN_LENGTH_MESSAGE };
  }
  if (name.length > PROJECT_NAME_MAX_LENGTH) {
    return { valid: false, message: PROJECT_NAME_MAX_LENGTH_MESSAGE };
  }
  if (isDuplicateProjectName(name, existingNames)) {
    return { valid: false, message: PROJECT_NAME_DUPLICATE_MESSAGE };
  }
  return { valid: true };
}

export function toCountryOptions(): Array<{ label: string; value: string }> {
  return PROJECT_COUNTRY_LIST.map((country) => ({ label: country, value: country }));
}

export function normalizeCountrySelection(value: any): string[] {
  if (!value) {
    return [];
  }
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((country: any) => (typeof country === 'string' ? country : (country?.value || country?.label || '')))
    .filter((country: string) => !!country);
}

export function getOffsetFromTimezone(timeZone: string): number {
  const subTimeZone = timeZone.substring(timeZone.indexOf('(') + 1, timeZone.length - 1);
  const hour = parseInt(subTimeZone.substring(1, subTimeZone.indexOf(':')), 10);
  const minutes = parseInt(subTimeZone.substring(subTimeZone.indexOf(':') + 1), 10);
  let offset = hour * 60 + minutes;
  if (subTimeZone[0] === '+') {
    offset *= -1;
  }
  return offset;
}

export function timezoneObjectFromName(timezoneName: string, timezoneObjects: any[]): any {
  if (!timezoneName) {
    return { ...DEFAULT_PROJECT_TIMEZONE };
  }
  const matched = timezoneObjects.find((tz) => tz.name === timezoneName);
  if (matched) {
    return matched;
  }
  const parts = timezoneName.split(' ');
  if (parts.length >= 2) {
    return {
      name: timezoneName,
      offset: getOffsetFromTimezone(parts[1]),
      timezone: parts[0]
    };
  }
  return { ...DEFAULT_PROJECT_TIMEZONE };
}

export function loadTimezoneOptions(): { names: Array<{ label: string; value: string }>; objects: any[] } {
  const objects: any[] = [];
  const names: Array<{ label: string; value: string }> = [];
  const momentTz = (window as any).moment;

  if (momentTz?.tz?.names) {
    momentTz.tz.names().forEach((timezone: string) => {
      const formatted = formatMomentTimezone(timezone, momentTz);
      objects.push(formatted);
      names.push({ label: formatted.name, value: formatted.name });
    });
    return { names, objects };
  }

  const fallback = [
    { tz: 'Asia/Kolkata', gmt: '+5:30', offset: -330 },
    { tz: 'America/New_York', gmt: '-5:00', offset: 300 },
    { tz: 'America/Los_Angeles', gmt: '-8:00', offset: 480 },
    { tz: 'Europe/London', gmt: '+0:00', offset: 0 },
    { tz: 'Europe/Paris', gmt: '+1:00', offset: -60 },
    { tz: 'Asia/Tokyo', gmt: '+9:00', offset: -540 },
    { tz: 'Australia/Sydney', gmt: '+10:00', offset: -600 }
  ];
  fallback.forEach((zone) => {
    const formatted = {
      name: `${zone.tz} GMT(${zone.gmt})`,
      offset: zone.offset,
      timezone: zone.tz
    };
    objects.push(formatted);
    names.push({ label: formatted.name, value: formatted.name });
  });
  return { names, objects };
}

function formatMomentTimezone(timezone: string, momentTz: any): any {
  let hrsMins: string;
  let offset = momentTz.tz.zone(timezone).offset(new Date());
  if (offset < 0) {
    hrsMins = '+';
    offset = offset + (-offset * 2);
  } else if (offset > 0) {
    hrsMins = '-';
  } else {
    hrsMins = '+';
  }
  const minute = offset % 60;
  const hour = (offset - minute) / 60;
  hrsMins = hrsMins + hour.toString() + ':' + (minute < 10 ? '0' : '') + minute.toString();
  return {
    name: timezone + ' GMT(' + hrsMins + ')',
    offset: momentTz.tz.zone(timezone).offset(new Date()),
    timezone
  };
}

export function isSelfServiceManager(): boolean {
  const HelpersRef = (window as any).Helpers;
  return typeof HelpersRef !== 'undefined' && typeof HelpersRef.isSelfServiceUser === 'function'
    ? !!HelpersRef.isSelfServiceUser()
    : false;
}
